import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

const STORAGE_KEYS = {
  token: "compliance_token",
  tenantId: "compliance_tenant_id",
  role: "compliance_role",
  mfa: "compliance_mfa",
} as const;

export type ComplianceRole =
  | "COMPLIANCE_ADMIN"
  | "FINANCE_REVIEWER"
  | "SHIPMENT_OPERATOR"
  | "AUDITOR"
  | "ROOT_ADMIN";

export interface ComplianceCredentials {
  token: string;
  tenantId: string;
  role: ComplianceRole;
  mfaVerified: boolean;
}

interface ComplianceAuthValue {
  credentials: ComplianceCredentials | null;
  isAuthenticated: boolean;
  login: (creds: ComplianceCredentials) => void;
  logout: () => void;
}

const ComplianceAuthContext = createContext<ComplianceAuthValue | null>(null);

function loadFromStorage(): ComplianceCredentials | null {
  const token = localStorage.getItem(STORAGE_KEYS.token);
  const tenantId = localStorage.getItem(STORAGE_KEYS.tenantId);
  const role = localStorage.getItem(STORAGE_KEYS.role) as ComplianceRole | null;
  if (!token || !tenantId || !role) return null;
  return {
    token,
    tenantId,
    role,
    mfaVerified: localStorage.getItem(STORAGE_KEYS.mfa) === "true",
  };
}

export function ComplianceAuthProvider({ children }: { children: ReactNode }) {
  const [credentials, setCredentials] = useState<ComplianceCredentials | null>(
    loadFromStorage,
  );

  function login(creds: ComplianceCredentials) {
    localStorage.setItem(STORAGE_KEYS.token, creds.token);
    localStorage.setItem(STORAGE_KEYS.tenantId, creds.tenantId);
    localStorage.setItem(STORAGE_KEYS.role, creds.role);
    localStorage.setItem(STORAGE_KEYS.mfa, String(creds.mfaVerified));
    setCredentials(creds);
  }

  function logout() {
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
    setCredentials(null);
  }

  return (
    <ComplianceAuthContext.Provider
      value={{
        credentials,
        isAuthenticated: credentials !== null,
        login,
        logout,
      }}
    >
      {children}
    </ComplianceAuthContext.Provider>
  );
}

export function useComplianceAuth(): ComplianceAuthValue {
  const ctx = useContext(ComplianceAuthContext);
  if (!ctx)
    throw new Error(
      "useComplianceAuth must be used inside ComplianceAuthProvider",
    );
  return ctx;
}
